import React from 'react';
import {
    Box,
    Card,
    Typography,
    IconButton,
    Chip,
    Tooltip,
    CardContent
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    PriorityHigh as RequiredIcon,
    ShortText as ShortTextIcon,
} from '@mui/icons-material';
import NotesIcon from '@mui/icons-material/Notes';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ListIcon from '@mui/icons-material/List';
import { ApplicationQuestion, QuestionType, MatchingImportance } from '../../../../types/Applications';

interface SingleQuestionPanelProps {
    question: ApplicationQuestion;
    onEditQuestion?: (questionId: string) => void;
    onDeleteQuestion?: (questionId: string) => void;
}

const SingleQuestionPanel: React.FC<SingleQuestionPanelProps> = ({
    question,
    onEditQuestion,
    onDeleteQuestion
}) => {
    // Function to get the appropriate icon for question type
    const getQuestionTypeIcon = (type: QuestionType) => {
        switch (type) {
            case 'short_answer':
                return <ShortTextIcon fontSize="small" />;
            case 'paragraph':
                return <NotesIcon fontSize="small" />;
            case 'multiple_choice':
                return <ListIcon fontSize="small" />;
            case 'multiple_select':
                return <CheckBoxIcon fontSize="small" />;
            case 'date':
                return <DateRangeIcon fontSize="small" />;
            case 'time':
                return <AccessTimeIcon fontSize="small" />;
            default:
                return <ShortTextIcon fontSize="small" />;
        }
    };

    // Function to get display name for question type
    const getQuestionTypeName = (type: QuestionType): string => {
        return type.split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Function to get color for matching importance
    const getMatchingColor = (importance: MatchingImportance): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
        switch (importance) {
            case 'critical':
                return 'error';
            case 'high':
                return 'warning';
            case 'medium':
                return 'info';
            case 'low':
                return 'success';
            default:
                return 'default';
        }
    };

    return (
        <Card 
            sx={{ 
                mb: 1, 
                borderRadius: 2,
                boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.05)', 
                '&:hover': {
                    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)'
                } 
            }}
        >
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                {/* Table-like row with columns */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    minHeight: 36 // Ensure consistent height
                }}>
                    {/* Column 1: Question Icon + Question Text */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        flexGrow: 1,
                        flexBasis: '50%',
                        minWidth: 0 // Important for text ellipsis to work
                    }}>
                        <Tooltip title={getQuestionTypeName(question.type)}>
                            <Box sx={{ color: 'primary.main', flexShrink: 0 }}>
                                {getQuestionTypeIcon(question.type)}
                            </Box>
                        </Tooltip>
                        
                        <Typography variant="body2" fontWeight={500} sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0
                        }}>
                            {question.question}
                        </Typography>
                    </Box>
                    
                    {/* Column 2: Question Type */}
                    <Box sx={{ width: '12%', flexShrink: 0, px: 1 }}>
                        <Tooltip title={`Question type: ${getQuestionTypeName(question.type)}`}>
                            <Typography variant="caption" color="text.secondary" noWrap>
                                {getQuestionTypeName(question.type)}
                            </Typography>
                        </Tooltip>
                    </Box>
                    
                    {/* Column 3: Matching Importance */}
                    <Box sx={{ width: '14%', flexShrink: 0, px: 1 }}>
                        {question.matching_importance !== 'none' && (
                            <Tooltip title={`Matching importance: ${question.matching_importance}`}>
                                <Chip
                                    label={question.matching_importance.charAt(0).toUpperCase() + question.matching_importance.slice(1)}
                                    size="small"
                                    color={getMatchingColor(question.matching_importance)}
                                    sx={{ 
                                        height: 20, 
                                        '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem', fontWeight: 500 } 
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                    
                    {/* Column 4: Role + Required */}
                    <Box sx={{ width: '12%', flexShrink: 0, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {question.required && (
                            <Tooltip title="Required">
                                <Box component="span" sx={{ color: 'secondary.main' }}>
                                    <RequiredIcon fontSize="small" />
                                </Box>
                            </Tooltip>
                        )}
                        
                        {question.for_role !== 'both' && (
                            <Tooltip title={`For ${question.for_role} only`}>
                                <Typography variant="caption" sx={{
                                    bgcolor: 'info.light',
                                    color: 'info.contrastText',
                                    px: 0.5,
                                    borderRadius: 0.5,
                                    fontSize: '0.65rem'
                                }}>
                                    {question.for_role.charAt(0).toUpperCase()}
                                </Typography>
                            </Tooltip>
                        )}
                    </Box>
                    
                    {/* Column 5: Actions */}
                    <Box sx={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title="Edit question">
                            <IconButton 
                                size="small" 
                                onClick={() => onEditQuestion && onEditQuestion(question.id)}
                                sx={{ p: 0.5, mr: 0.5 }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete question">
                            <IconButton 
                                size="small" 
                                onClick={() => onDeleteQuestion && onDeleteQuestion(question.id)}
                                color="error"
                                sx={{ p: 0.5 }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default SingleQuestionPanel;